import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormFeatureEmbeddedComponent, GridComponent, ICustomWindow, IFormFeatureEmbedded, TextHtmlComponent, WindowRefService } from '@sirius/common';
import {
    FORM_SALESFORCE_IDENTIFIERS,
    MatchmakingFormConfig,
    PardotFormRequest,
} from 'matchmaking/models';
import {
    MatchmakingApiService,
    MatchmakingDataService,
    MatchmakingTranslateService,
} from 'matchmaking/services';
import { FormularMetaDataService } from './formular-meta-data/formular-meta-data.service';

@Component({
    selector: 'matchmaking-formular',
    imports: [FormFeatureEmbeddedComponent, GridComponent, TextHtmlComponent],
    templateUrl: './formular.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './formular.component.scss'
})
export class FormularComponent implements OnInit, OnDestroy {
    translateService = inject(MatchmakingTranslateService);
    dataService = inject(MatchmakingDataService);
    apiService = inject(MatchmakingApiService);
    metaDataService = inject(FormularMetaDataService);
    windowRef = inject(WindowRefService);

    formConfig: WritableSignal<IFormFeatureEmbedded | undefined> = signal(undefined);

    translations = computed(() => this.translateService.translations());

    private get Window(): ICustomWindow | undefined {
        return this.windowRef.NativeWindow;
    }

    ngOnInit() {
        this.setupMessageListener();
        this.initializeFormConfig();
    }

    private setupMessageListener() {
        if (this.Window) {
            window.addEventListener('message', this.messageHandler);
        }
    }

    ngOnDestroy() {
        if (this.Window) {
            window.removeEventListener('message', this.messageHandler);
        }
    }

    private messageHandler = async (event: MessageEvent) => {
        if (event.data.tracking?.event === 'form_complete') {
            // Request animation here ensures the page redirect happens after the tracking has happened
            requestAnimationFrame(() => {
                this.dataService.setFormHasSubmitted();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }

        if (event.data.type === 'needValidation' && event.source) {
            await this.handleFormValidation(event);
        }
    };

    private async handleFormValidation(event: MessageEvent): Promise<void> {
        const data = event.data.data as PardotFormRequest;

        const request: Partial<MatchmakingFormConfig> = {
            firstName: data.first_name,
            lastName: data.last_name,
            country: data.country,
            region: data.state_region_us ?? data.state_region_ca,
            phoneNo: data.phone,
            workEmail: data.email,
            worktitle: data.job_title,
            userRole: data.user_role,
            company: data.company,
        };

        this.dataService.updateConfig({ ...request });
        await this.apiService.submit();

        event.source?.postMessage(
            {
                type: 'validation',
                isValid: false,
            },
            { targetOrigin: event.origin },
        );
    }

    private initializeFormConfig(): void {
        const config = this.dataService.config();
        if (!config) return;

        const readableMetadata = this.metaDataService.buildReadableFormMetadata(
            config,
            this.dataService.applicationAsProperty(),
        );

        const application = this.dataService.getSelectedApplication();
        const industry = this.dataService.getSelectedIndustry();

        if (!application || !industry) {
            throw new Error('Missing industry or application');
        }

        const analyticsMetaData = this.metaDataService.buildAnalyticsMetaData(
            industry.externalId,
            application.externalId,
            config,
        );

        const timelineSalesforceValue = this.metaDataService.getTimelineSalesforceValue(
            this.dataService.config()?.timeline || '',
        );

        this.formConfig.set({
            componentName: 'feature-form-embed',
            form: {
                formId: 1,
                name: '',
                embedCode:
                    '<iframe src="https://go.universal-robots.com/l/1024783/2024-12-13/295q2?culture=en-US&formId=13639&matchmaking=1" width="100%" height="500" type="text/html" frameborder="0" allowTransparency="true" style="border: 0"></iframe>',
                initValues: {
                    formFields: [
                        {
                            name: FORM_SALESFORCE_IDENTIFIERS.TIMELINE,
                            value: timelineSalesforceValue || '',
                        },
                        {
                            name: FORM_SALESFORCE_IDENTIFIERS.APPLICATION,
                            value: application.salesforceMapping || '',
                        },
                        {
                            name: FORM_SALESFORCE_IDENTIFIERS.INDUSTRY,
                            value: industry.salesforceMapping || '',
                        },
                        {
                            name: FORM_SALESFORCE_IDENTIFIERS.COMMENTS,
                            value: readableMetadata + ' -- ' + analyticsMetaData,
                        },
                    ],
                },
            },
            title: '',
            textHtml: '',
        });
    }
}
